import React, { useState, useMemo } from "react";
import terminalService from "../../services/terminalService";

import "./POSProductList.css";

// Helper function to determine sort order for prices
const getPriceOrder = (price) => {
  if (!price.recurring) return 100;
  const { interval, interval_count } = price.recurring;
  if (interval === "month") {
    if (interval_count === 1) return 1;
    if (interval_count === 3) return 2;
    if (interval_count === 6) return 3;
  }
  if (interval === "year") return 4;
  return 100;
};

const POSProductList = ({ products, addToCart }) => {
  // Add debug logging when component receives products
  //console.log(`POSProductList received ${products.length} products`);
  // Format price from cents to dollars
  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Group products by category
  const groupedProducts = useMemo(() => {
    const groups = {};
    products.forEach((product) => {
      const category = product.category || "Uncategorized";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(product);
    });
    return groups;
  }, [products]);

  // State to track selected price for each product
  const [selectedPrices, setSelectedPrices] = useState({});

  // Handle price selection
  const handlePriceSelect = (e, productId) => {
    e.stopPropagation(); // Prevent card click
    setSelectedPrices({
      ...selectedPrices,
      [productId]: parseInt(e.target.value),
    });
  };

  // Handle add to cart with selected price
  const handleAddToCart = async (e, product) => {
    e.stopPropagation(); // Prevent card click

    // Check if this is a membership product
    const isMembership =
      product.metadata?.productType?.toLowerCase()?.includes("membership") ||
      product.category?.toLowerCase() === "membership" ||
      product.name?.toLowerCase()?.includes("membership");

    // Add detailed logging here
    /*console.log("Product ID:", product.id);
    console.log("Product:", product);
    console.log("Is membership?", isMembership);
    console.log("Selected prices state:", selectedPrices);
    console.log("Selected price for this product:", selectedPrices[product.id]);*/

    // If product has multiple prices and one is selected, use it
    if (product.prices && product.prices.length > 1) {
      // Get the selected price or use the default price
      const priceToUse = selectedPrices[product.id] || product.price;
      //console.log("Price to use:", priceToUse);

      const selectedPrice = product.prices.find(
        (price) => price.unit_amount === priceToUse
      );

      if (selectedPrice) {
        /*console.log("Selected price:", selectedPrice);
        console.log("Has recurring billing?", !!selectedPrice.recurring);*/

        // Add product with selected price (no proration)
        const productWithSelectedPrice = {
          ...product,
          price: selectedPrice.unit_amount,
          selectedPriceId: selectedPrice.id,
          recurring: selectedPrice.recurring,
          metadata: {
            ...(product.metadata || {}),
            ...(selectedPrice.metadata || {}),
          },
          priceDetails: {
            id: selectedPrice.id,
            amount: selectedPrice.unit_amount,
            recurring: selectedPrice.recurring,
            metadata: selectedPrice.metadata || {},
          },
        };
        /* console.log(
          "Adding product with selected price:",
          productWithSelectedPrice
        );*/
        addToCart(productWithSelectedPrice);
        return;
      }
    }

    // Otherwise use default price and preserve default price metadata when present.
    addToCart({
      ...product,
      metadata: {
        ...(product.metadata || {}),
        ...(product.default_price?.metadata || {}),
      },
    });
  };

  return (
    <div className="pos-product-list">
      {products.length === 0 ? (
        <div className="pos-no-products">
          <p>
            No products found. Try adjusting your search or category filter.
          </p>
        </div>
      ) : (
        <div>
          {Object.entries(groupedProducts).map(
            ([category, categoryProducts]) => (
              <div key={category} className="pos-category-group">
                <h2 className="pos-category-header">{category}</h2>
                <div className="pos-products-grid">
                  {categoryProducts.map((product) => (
                    <div key={product.id} className="pos-product-card">
                      {product.image && (
                        <div className="pos-product-image">
                          <img src={product.image} alt={product.name} />
                        </div>
                      )}
                      <div className="pos-product-info">
                        <h3 className="pos-product-name">{product.name}</h3>

                        {/* Price display and selection */}
                        {product.prices && product.prices.length > 1 ? (
                          <div className="pos-product-price-selector">
                            <select
                              value={
                                selectedPrices[product.id] || product.price
                              }
                              onChange={(e) => handlePriceSelect(e, product.id)}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {[...product.prices]
                                .sort(
                                  (a, b) => getPriceOrder(a) - getPriceOrder(b)
                                )
                                .map((price) => {
                                  // Create descriptive label for recurring prices
                                  let recurringLabel = "";
                                  if (price.recurring) {
                                    const { interval, interval_count } =
                                      price.recurring;
                                    if (interval === "month") {
                                      if (interval_count === 1) {
                                        recurringLabel = " / Monthly";
                                      } else if (interval_count === 3) {
                                        recurringLabel = " / Every 3 Months";
                                      } else if (interval_count === 6) {
                                        recurringLabel = " / Every 6 Months";
                                      } else {
                                        recurringLabel = ` / ${interval_count} months`;
                                      }
                                    } else if (interval === "year") {
                                      recurringLabel = " / Yearly";
                                    } else {
                                      recurringLabel = ` / ${interval_count} ${interval}${
                                        interval_count > 1 ? "s" : ""
                                      }`;
                                    }
                                  }

                                  return (
                                    <option
                                      key={price.id}
                                      value={price.unit_amount}
                                    >
                                      {formatPrice(price.unit_amount)}
                                      {recurringLabel}
                                    </option>
                                  );
                                })}
                            </select>
                          </div>
                        ) : (
                          <p className="pos-product-price">
                            {formatPrice(product.price)}
                          </p>
                        )}

                        {product.sku && (
                          <p className="pos-product-sku">SKU: {product.sku}</p>
                        )}
                        <div className="pos-product-category">
                          {product.category}
                        </div>
                      </div>
                      <button
                        className="pos-add-to-cart-btn"
                        onClick={(e) => handleAddToCart(e, product)}
                      >
                        <span>Add to Cart</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default POSProductList;
